import {
    AttributesData,
    DefaultValueTypeDefinitions,
    InputDefinition,
    InputType,
    SingleValueDataType,
    TaskAttribute,
    UpdatedValueDataType,
    ValueDataType,
} from 'Types/Tasks.types'
import {
    DEFAULT_ERROR_MESSAGE,
    INPUT_TYPE_DATE_FORMAT,
    INPUT_TYPE_TIME_FORMAT,
    LogicalOperationValueType,
} from 'Constants/constants.ts'
import { AttributeValueType, AttributeValueTypeDefinitions } from 'Types/Attribute.types'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import { message } from 'antd/lib'
import { MutableRefObject } from 'react'

const isNumber = (value: unknown): value is number => typeof value === 'number'
const isString = (value: unknown): value is string => typeof value === 'string'
const isNumberArray = (value: unknown): value is number[] =>
    Array.isArray(value) && value.every(isNumber)

export const getAttributeSupportedLogicalOperations = (
    attribute: TaskAttribute
): LogicalOperationValueType[] | undefined => {
    const inputType = attribute.input_definition?.input_type
    if (!inputType) return
    let componentType
    switch (inputType) {
        case InputType.SELECT:
            componentType = attribute.input_definition?.component_type
            switch (componentType) {
                case 'DROPDOWN':
                case 'RADIO_BUTTON':
                    return ['EQUAL_TO_ANY', 'NOT_EQUAL_TO_ANY']
            }
            break
        case InputType.MULTI_SELECT:
            componentType = attribute.input_definition?.component_type
            switch (componentType) {
                case 'DROPDOWN':
                case 'CHECKLIST':
                    return ['CONTAINS_ANY', 'CONTAINS_ALL', 'DOES_NOT_CONTAIN_ANY']
            }
            break
        case InputType.TEXT:
        case InputType.STRING_ARRAY:
            return ['CONTAINS', 'DOES_NOT_CONTAIN']
        case InputType.INT_SCALE:
        case InputType.DATE:
        case InputType.INT:
        case InputType.TIME:
            return ['EQUAL_TO_ANY', 'NOT_EQUAL_TO_ANY', 'GREATER_THAN', 'LESS_THAN', 'BETWEEN']
    }
}

export const performLogicalOperation = (
    inputType: InputType,
    logicalOperation: LogicalOperationValueType,
    comparisonValue: AttributeValueType | undefined,
    referenceValue: Omit<AttributeValueType, 'undefined'>
): boolean => {
    if (!isNumber(comparisonValue) && isEmpty(comparisonValue)) return false
    const formatDateOrTime = (value: string, inputType: InputType.DATE | InputType.TIME) => {
        const dateOrTimeFormats = {
            [InputType.DATE]: INPUT_TYPE_DATE_FORMAT,
            [InputType.TIME]: INPUT_TYPE_TIME_FORMAT,
        }
        return dayjs(value, dateOrTimeFormats[inputType])
    }

    const isValidDateOrTime = () => {
        return (
            comparisonValue: string,
            referenceValue: string,
            inputType: InputType.DATE | InputType.TIME
        ): boolean =>
            formatDateOrTime(comparisonValue, inputType) >
            formatDateOrTime(referenceValue, inputType)
    }

    try {
        switch (logicalOperation) {
            case 'GREATER_THAN':
                if (inputType === InputType.DATE || inputType === InputType.TIME) {
                    return isValidDateOrTime()(
                        comparisonValue as string,
                        referenceValue as string,
                        inputType
                    )
                } else if (isNumber(comparisonValue) && isNumber(referenceValue)) {
                    return comparisonValue > referenceValue
                }
                break
            case 'LESS_THAN':
                if (inputType === InputType.DATE || inputType === InputType.TIME) {
                    return isValidDateOrTime()(
                        referenceValue as string,
                        comparisonValue as string,
                        inputType
                    )
                } else if (isNumber(comparisonValue) && isNumber(referenceValue)) {
                    return comparisonValue < referenceValue
                }
                break
            case 'BETWEEN':
                if (inputType === InputType.DATE || inputType === InputType.TIME) {
                    if (Array.isArray(referenceValue) && referenceValue.length === 2) {
                        const [start, end] = referenceValue as [string, string]
                        return (
                            formatDateOrTime(comparisonValue as string, inputType) >=
                                formatDateOrTime(start, inputType) &&
                            formatDateOrTime(comparisonValue as string, inputType) <=
                                formatDateOrTime(end, inputType)
                        )
                    }
                } else if (
                    isNumber(comparisonValue) &&
                    isNumberArray(referenceValue) &&
                    referenceValue.length === 2
                ) {
                    const [min, max] = referenceValue
                    return comparisonValue >= min && comparisonValue <= max
                }
                break
            case 'CONTAINS':
                // citations
                if (Array.isArray(comparisonValue) && isString(referenceValue)) {
                    return comparisonValue.some(v => (v as string).includes(referenceValue))
                } else if (isString(comparisonValue) && isString(referenceValue)) {
                    return comparisonValue.includes(referenceValue)
                }
                break
            case 'CONTAINS_ALL':
                if (Array.isArray(comparisonValue) && Array.isArray(referenceValue)) {
                    return (referenceValue as string[]).every(v =>
                        (comparisonValue as string[]).includes(v)
                    )
                }
                break
            case 'CONTAINS_ANY':
                if (Array.isArray(comparisonValue) && Array.isArray(referenceValue)) {
                    return (referenceValue as string[]).some(v =>
                        (comparisonValue as string[]).includes(v)
                    )
                }
                break
            case 'EQUAL_TO_ANY':
                if (Array.isArray(referenceValue)) {
                    return referenceValue.includes(comparisonValue)
                }
                break
            case 'NOT_EQUAL_TO_ANY':
                if (Array.isArray(referenceValue)) {
                    return !referenceValue.includes(comparisonValue)
                }
                break
            case 'DOES_NOT_CONTAIN':
                // citations
                if (Array.isArray(comparisonValue) && isString(referenceValue)) {
                    return !comparisonValue.some(v => (v as string).includes(referenceValue))
                } else if (isString(comparisonValue) && isString(referenceValue)) {
                    return !comparisonValue.includes(referenceValue)
                }
                break
            case 'DOES_NOT_CONTAIN_ANY':
                if (Array.isArray(comparisonValue) && Array.isArray(referenceValue)) {
                    return !(referenceValue as string[]).some(v =>
                        (comparisonValue as string[]).includes(v)
                    )
                }
                break
            default: {
                const err = new Error()
                err.name = 'UnsupportedLogicalOperationType'
                throw err
            }
        }
    } catch (error) {
        message.error(DEFAULT_ERROR_MESSAGE)
        let errorMessage =
            'Logical operation between comparison value and reference value failed due to unsupported value types'
        if ((error as Error).name === 'UnsupportedLogicalOperationType') {
            errorMessage =
                'Logical operation between comparison value and reference value is invalid'
        }
        throw new Error(errorMessage)
    }
    return false
}

export const validateOperator = (attribute: TaskAttribute, operator: LogicalOperationValueType) => {
    const possibleOperations = getAttributeSupportedLogicalOperations(attribute)
    if (!possibleOperations?.includes(operator)) {
        throw new Error('Depends on attribute has invalid logical operations')
    }
}

export const clearAttributeValueIfNecessary = (
    toUpdateRef: MutableRefObject<Record<string, AttributeValueType | undefined>>,
    values: AttributesData,
    attribute: TaskAttribute
) => {
    const currentAttributeValue = values?.[attribute.name]
    if (currentAttributeValue !== undefined) {
        toUpdateRef.current[attribute.name] = undefined
    }
}

export const findDependsOnAttribute = (attributes: TaskAttribute[], version_id: string) => {
    return attributes.find((attribute: TaskAttribute) => attribute.version_id === version_id)
}

export const getDependsOnAttributeValue = (
    values: AttributesData,
    attribute: TaskAttribute
): AttributeValueType => {
    return values?.[attribute.name]?.data.value
}

export const showAttribute = (
    attributes: TaskAttribute[],
    currentAttribute: TaskAttribute,
    attributesOnUI: { [key: string]: 1 },
    values: AttributesData,
    toUpdateRef: MutableRefObject<Record<string, AttributeValueType | undefined>>
): boolean => {
    if (!currentAttribute.visibility_condition) return true
    const visibilityCondition = currentAttribute.visibility_condition
    const dependsOnAttribute = findDependsOnAttribute(
        attributes,
        visibilityCondition.depends_on_attribute_version_id
    )

    if (!dependsOnAttribute) {
        message.destroy()
        message.error(DEFAULT_ERROR_MESSAGE)
        throw new Error('Conditional attribute exists without depends on attribute')
    }

    const dependsOnAttributeValue = getDependsOnAttributeValue(values, dependsOnAttribute)
    if (!attributesOnUI[dependsOnAttribute.name]) {
        clearAttributeValueIfNecessary(toUpdateRef, values, currentAttribute)
        return false
    }

    validateOperator(dependsOnAttribute, visibilityCondition.operator)

    const isVisible = performLogicalOperation(
        (dependsOnAttribute.input_definition as InputDefinition).input_type,
        visibilityCondition.operator,
        dependsOnAttributeValue,
        visibilityCondition.value
    )

    if (!isVisible) {
        clearAttributeValueIfNecessary(toUpdateRef, values, currentAttribute)
    }

    return isVisible
}

export const getDefaultAttributeValue = (valueType: keyof AttributeValueTypeDefinitions) => {
    if (valueType === 'INT') return
    return DefaultValueTypeDefinitions[valueType]
}

export function convertFlatToWrappedAttributes<T extends keyof AttributeValueTypeDefinitions>(
    flatAttributes:
        | Record<string, UpdatedValueDataType<T>>
        | Record<string, SingleValueDataType<T>>
        | undefined
): Record<
    string,
    UpdatedValueDataType<T> extends { value_id: string }
        ? ValueDataType<T>
        : { data: SingleValueDataType<T> }
> {
    const wrapped: Record<
        string,
        UpdatedValueDataType<T> extends { value_id: string }
            ? ValueDataType<T>
            : { data: SingleValueDataType<T> }
    > = {}

    if (!flatAttributes) return wrapped

    for (const key in flatAttributes) {
        const attr = flatAttributes[key]

        if ('value_id' in attr) {
            wrapped[key] = {
                value_id: attr.value_id,
                data: {
                    value: attr.value,
                    value_type: attr.value_type,
                },
            } as ValueDataType<T>
        } else {
            wrapped[key] = {
                data: {
                    value: attr.value,
                    value_type: attr.value_type,
                },
            }
        }
    }

    return wrapped
}

export const getFileTypeByExtension = (fileName: string) => {
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg|tiff|ico)$/i
    const audioExtensions = /\.(mp3|wav|ogg|flac|aac|m4a|wma)$/i
    const videoExtensions = /\.(mp4|avi|mov|wmv|flv|mkv|webm)$/i

    if (imageExtensions.test(fileName.toLowerCase())) {
        return 'image'
    } else if (audioExtensions.test(fileName.toLowerCase())) {
        return 'audio'
    } else if (videoExtensions.test(fileName.toLowerCase())) {
        return 'video'
    } else {
        return 'file'
    }
}

export const getJSONPathFromPath = (path: string, index: number) => {
    return path.replace('$', `$.turn[${index}]`)
}
